import { useState, useEffect } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import ClientService, {
	ClientPermissions,
	IClient,
} from '../../../../services/Client.service';
import Regex from '../../../../utils/regex';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';

export interface ClientModalProps extends ModalProps<IClient> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IClient>>>;
}

const ClientsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	...props
}: ClientModalProps) => {
	const { t } = useTranslation();

	const permissionsName: { [key in keyof ClientPermissions]: string } = {
		isMaintenance: t('maintenance.title'),
		isMonitoring: t('monitor.title'),
		isMovement: t('movement.title'),
		isReport: t('reports.title'),
	};

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') return onEdit();
		return onCreate();
	};

	const validate = (): boolean => {
		if (
			!data.corporateName ||
			!data.fantasyName ||
			!data.cnpj ||
			!data.address ||
			!data.maintenanceFee ||
			!data.monitoringFee ||
			!data.maxDailyMonitoringsAM1 ||
			!data.maxDailyMonitoringsPM1 ||
			!data.maxDailyMonitoringsAM2 ||
			!data.maxDailyMonitoringsPM2
		) {
			setError('alert.error.mandatory');
			return false;
		}
		if (!Regex.CNPJ(data.cnpj)) {
			setError('alert.error.cnpj');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await ClientService.create(data, setError);
		if (response) {
			NotificationToast.Success(
				`${t('admin.client')} ${t('alert.success.create')}`
			);
			onClose(true);
		}
	};

	const onEdit = async () => {
		const response = await ClientService.edit(data, setError);
		if (response) {
			NotificationToast.Success(
				`${t('admin.client')} ${t('alert.success.update')}`
			);
			onClose(true);
		}
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const handlePermissions = (e: EditableChangeEvent) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				permissions: {
					...prev.data.permissions,
					[e.target.name]: e.target.value,
				},
			},
		}));
	};

	const renderToggles = () => (
		<div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
			{Object.entries(data.permissions).map(([name, value]) => (
				<Toggle
					key={name}
					name={name}
					value={value}
					label={permissionsName[name as keyof ClientPermissions]}
					onChange={handlePermissions}
					disabled={mode === 'view'}
				/>
			))}
			<Toggle
				name="moduloAuditoriaAtivado"
				value={data.moduloAuditoriaAtivado}
				label={t('admin.clients.modulo_auditoria')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="processoFluxoUnicoAtivado"
				value={data.processoFluxoUnicoAtivado}
				label={t('admin.clients.container_tracking')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="supplyReturnAtivado"
				value={data.supplyReturnAtivado}
				label={t('admin.clients.supply_return')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid grid-cols-2 gap-x-2 gap-y-3">
			<Input
				containerClassName="col-span-full"
				value={data.fantasyName}
				name="fantasyName"
				label={t('admin.clients.fantasy_name')}
				placeholder={t('admin.clients.fantasy_name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.corporateName}
				name="corporateName"
				label={t('admin.clients.corporate_name')}
				placeholder={t('admin.clients.corporate_name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.cnpj}
				name="cnpj"
				label={t('admin.clients.cnpj')}
				placeholder={t('admin.clients.cnpj')}
				onChange={handleInput}
				disabled={mode === 'view'}
				mask="99.999.999/9999-99"
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.address}
				name="address"
				label={t('misc.address')}
				placeholder={t('misc.address')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				value={data.maxDailyMonitoringsAM1 || ''}
				name="maxDailyMonitoringsAM1"
				label={`${t('admin.clients.monitoring_am')} 1`}
				placeholder={`${t('admin.clients.monitoring_am')} 1`}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.maxDailyMonitoringsAM2 || ''}
				name="maxDailyMonitoringsAM2"
				label={`${t('admin.clients.monitoring_am')} 2`}
				placeholder={`${t('admin.clients.monitoring_am')} 2`}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.maxDailyMonitoringsPM1 || ''}
				name="maxDailyMonitoringsPM1"
				label={`${t('admin.clients.monitoring_pm')} 1`}
				placeholder={`${t('admin.clients.monitoring_pm')} 1`}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.maxDailyMonitoringsPM2 || ''}
				name="maxDailyMonitoringsPM2"
				label={`${t('admin.clients.monitoring_pm')} 2`}
				placeholder={`${t('admin.clients.monitoring_pm')} 2`}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.monitoringFee || ''}
				name="monitoringFee"
				label={t('admin.clients.monitoring_fee')}
				placeholder={t('admin.clients.monitoring_fee')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required
			/>
			<Input
				value={data.maintenanceFee || ''}
				name="maintenanceFee"
				label={t('admin.clients.maintenance_fee')}
				placeholder={t('admin.clients.maintenance_fee')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="number"
				required={true}
			/>
		</div>
	);

	return (
		<Modal<IClient>
			className=""
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default ClientsModal;
